/**
 * Searchable checklist of the person's open activities, used to say "I also did these during this
 * time" when logging a session. Reads activities from the task store; the server decides what the
 * concurrent sessions record.
 *
 * @module apps/web/src/components/activities/ConcurrentActivityPicker
 */

import { useId, useMemo, useState } from 'react';
import type { TaskUI } from '@fluxability/types';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useTasksStore } from '@/stores/tasksStore';

/** Most activities the server accepts as concurrent with one log (`MAX_CONCURRENT_ACTIVITIES`). */
export const MAX_CONCURRENT_ACTIVITIES = 8;

/** Rows shown before the person narrows the list by typing. */
const VISIBLE_ROWS = 50;

/** Selection inputs for the concurrent-activity checklist. */
interface ConcurrentActivityPickerProps {
  /** Activity the session is being logged for; it is never offered. */
  primaryTaskId: string | undefined;
  /** Currently chosen companion activity ids, in the order chosen. */
  selectedIds: string[];
  /** Receive the next selection; the picker never exceeds MAX_CONCURRENT_ACTIVITIES. */
  onChange: (ids: string[]) => void;
}

/** Whether an activity can still receive logged time: not finished or cancelled. */
export function isOpenActivity(task: TaskUI): boolean {
  return !['completed', 'done', 'cancelled'].includes(task.status);
}

/** Render the filterable checklist of open activities other than the primary one. */
export function ConcurrentActivityPicker({
  primaryTaskId,
  selectedIds,
  onChange,
}: ConcurrentActivityPickerProps) {
  const tasks = useTasksStore(s => s.tasks);
  const [query, setQuery] = useState('');
  const inputId = useId();
  const candidates = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return tasks
      .filter(task => task.id !== primaryTaskId && isOpenActivity(task))
      .filter(task => !needle || task.title.toLowerCase().includes(needle))
      .slice(0, VISIBLE_ROWS);
  }, [tasks, primaryTaskId, query]);
  const full = selectedIds.length >= MAX_CONCURRENT_ACTIVITIES;

  /** Add or remove one activity from the selection. */
  function toggle(id: string, checked: boolean) {
    if (checked && !selectedIds.includes(id) && !full) onChange([...selectedIds, id]);
    if (!checked) onChange(selectedIds.filter(selected => selected !== id));
  }

  return (
    <div className="grid gap-2">
      <Input
        id={inputId}
        value={query}
        onChange={event => setQuery(event.target.value)}
        placeholder="Search your activities"
        aria-label="Search activities done during this time"
      />
      <div className="native-scroll max-h-40 overflow-y-auto rounded-md border" role="group">
        {candidates.length === 0 ? (
          <p className="text-muted-foreground p-3 text-xs">No other open activities match.</p>
        ) : (
          candidates.map(task => {
            const checked = selectedIds.includes(task.id);
            return (
              <label
                key={task.id}
                className="hover:bg-secondary flex min-h-10 cursor-pointer items-center gap-2 px-3 py-1.5 text-sm"
              >
                <Checkbox
                  checked={checked}
                  disabled={!checked && full}
                  onCheckedChange={value => toggle(task.id, value === true)}
                />
                <span className="truncate">{task.title}</span>
              </label>
            );
          })
        )}
      </div>
      <p className="text-muted-foreground text-xs">
        {selectedIds.length > 0
          ? `Also logs this time for ${selectedIds.length} more ${selectedIds.length === 1 ? 'activity' : 'activities'}. Progress and ratings stay with this one.`
          : 'Optional. Pick anything you did at the same time, like a lecture during a run.'}
      </p>
    </div>
  );
}
